import { createAsyncThunk, AnyAction } from "@reduxjs/toolkit"
import { ThunkDispatch } from "redux-thunk"

import { API } from "redux/api"
import i18n from "config/i18n"
import { CONSENT_STATUS_DONE, POLLING_INTERVAL, POLLING_TIMEOUT } from "config"
import PollingService from "redux/api/pollingService"
import {
  start as startVerification,
  reset as resetVerification,
} from "redux/features/verification/verificationSlice"
import { start as startPolling } from "redux/features/polling/pollingSlice"
import { fetchConsent } from "redux/features/consent/consentThunk"
import { ConsentSignValues } from "types"

export const createConsentSignSession = createAsyncThunk(
  "consent-sign-session/create",
  async (values: ConsentSignValues) => {
    const response = await API.post("/consent/sign", values)
    return response.data
  },
)

export const fetchConsentSignSession = createAsyncThunk(
  "consent-sign-session/fetch",
  async (values: { signing_method: string; session_id: string }) => {
    const response = await API.get(
      `/consent/sign/${values.signing_method}/session/${values.session_id}`,
    )

    return response.data
  },
)

export const makeSignConsent = (values: ConsentSignValues) => {
  return function (dispatch: ThunkDispatch<unknown, unknown, AnyAction>) {
    return new Promise((resolve, reject) => {
      return dispatch(createConsentSignSession(values))
        .then(
          ({ payload }) => {
            if (!payload) {
              return reject(i18n.t("notice.sign.session.unavailable"))
            }

            const { session_id, verification_code } = payload.sign
            dispatch(startVerification({ code: verification_code }))

            const timerId = setInterval(async () => {
              const pollingId = PollingService.getPolling()

              if (!pollingId) {
                clearInterval(timerId)
                return reject(i18n.t("common.request_cancelled"))
              }

              const { signing_method } = values
              const result = await dispatch(
                fetchConsentSignSession({ signing_method, session_id }),
              )

              if (result?.payload?.sign?.status === CONSENT_STATUS_DONE) {
                clearInterval(timerId)
                PollingService.resetPolling()

                dispatch(resetVerification())
                dispatch(fetchConsent())

                return resolve("success")
              }
            }, POLLING_INTERVAL)

            dispatch(startPolling({ pollingId: timerId }))

            setTimeout(() => {
              clearInterval(timerId)
              PollingService.resetPolling()
            }, POLLING_TIMEOUT)
          },
          () => {
            reject(i18n.t("notice.sign.session.failure"))
          },
        )
        .catch((error) => {
          reject(error)
        })
    })
  }
}
